import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { z } from 'zod';

import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
} from '../../../hooks/Analytics';
import { usePageContext } from '../../../hooks/PageContext/usePageContext';
import { useTheme } from '../../../hooks/useTheme';
import { useSettings } from '../../../hooks/useSettings';
import globalStyleString from '../../../lib/globalStyleString';
import styles from './CpCustomComponent.module.css';
import { FeatureLayout } from '../DynamicLayout/layoutTree';

export const PING_INTERVAL_MS = 300;

export interface MessageProps {
  guestReadyToReceiveMessages?: boolean;
  guestContentLoaded?: boolean;
  guestDocumentSize?: string;
  parentWindowId?: string;
  isHelpWidgetOpen?: boolean;
}

export interface CpCustomComponentProps {
  featureName?: string;
  componentId: string;
  layout?: FeatureLayout | null | undefined;
  postMessageRef?: (postMessage: (message: unknown) => void) => void;
  onIsHelpWidgetOpenChange?: (isHelpWidgetOpen: boolean) => void;
  jwt?: string;
  experiments?: Record<string, unknown>;
  transferredState?: unknown;
}

// Schemas for a `customComponents` in `settings_json` (RetailerSettings)
const customComponentSchema = z.object({
  name: z.string(),
  url: z.string(),
  versionId: z.number(),
  scopes: z.string().array(),
  settings: z.record(z.union([z.string(), z.number(), z.boolean()])),
});

const settingsResponseSchema = z.object({
  customComponents: z.record(z.unknown()),
});

function pick<T extends {}>(object: T, keys: string[]): Partial<T> {
  const entries = Object.entries(object).filter(([key]) => keys.includes(key));
  return Object.fromEntries(entries) as Partial<T>;
}

const customComponentTrackingEventSchema = z.object({
  eventType: z.union([
    z.literal(NarvarEventTypes.COMPONENT_RENDER),
    z.literal(NarvarEventTypes.CLICK),
  ]),
  clickSubType: z.string().optional(),
});

const CpCustomComponent = ({
  featureName,
  componentId,
  layout,
  postMessageRef,
  onIsHelpWidgetOpenChange,
  jwt,
  experiments,
  transferredState,
}: CpCustomComponentProps) => {
  const theme = useTheme();
  const { panelcontainerNoneNone } = theme;
  const {
    borderTopLeftRadius = 0,
    borderTopRightRadius = 0,
    borderBottomLeftRadius = 0,
    borderBottomRightRadius = 0,
  } = panelcontainerNoneNone || {}; // can be undefined or null

  const { state } = usePageContext();

  const settingsResponse = useSettings();
  const guestComponentSettings = useMemo(() => {
    // `useSettings` will default to an empty object if the query fails to
    // return RetailerSettings. Also, if an experience does not have any
    // custom components defined, it will be set to `null`. Check for both
    // before attempting to get customComponent settings.
    if (
      Object.keys(settingsResponse).length > 0 &&
      settingsResponse.customComponents !== null
    ) {
      try {
        const validatedSettingsResponse =
          settingsResponseSchema.parse(settingsResponse);

        if (
          validatedSettingsResponse.customComponents[componentId] !== undefined
        ) {
          const validatedCustomComponent = customComponentSchema.parse(
            validatedSettingsResponse.customComponents[componentId],
          );

          return validatedCustomComponent;
        }
      } catch (e) {
        if (e instanceof Error) {
          console.error(`Invalid custom component settings: ${e.message}`);
        } else {
          console.error('Invalid custom component settings');
        }
      }
    }
  }, [componentId, settingsResponse]);

  const [guestReadyForMessages, setGuestReady] = useState(false);
  const [guestIsLoaded, setGuestContentLoaded] = useState(false);
  const [iFrameHeight, setIFrameHeight] = useState('0');

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const pingGuestInterval = useRef<number | undefined>(undefined);

  const logEvent = useAnalyticsPost();

  useEffect(() => {
    if (guestComponentSettings === undefined) return;

    const guestAppUrl = guestComponentSettings.url;

    const receiveMessage = ({ data, origin }: MessageEvent) => {
      const {
        guestReadyToReceiveMessages,
        guestContentLoaded,
        guestDocumentSize,
        isHelpWidgetOpen,
        parentWindowId,
        trackEvent,
      } = data;

      if (!guestAppUrl.includes(origin)) return;
      if (parentWindowId !== componentId) return;

      if (guestReadyToReceiveMessages) setGuestReady(true);
      if (guestContentLoaded) setGuestContentLoaded(true);
      if (guestDocumentSize) setIFrameHeight(guestDocumentSize);

      if (isHelpWidgetOpen !== undefined && onIsHelpWidgetOpenChange) {
        onIsHelpWidgetOpenChange(isHelpWidgetOpen as boolean);
      }

      if (trackEvent !== undefined) {
        try {
          const eventValidated =
            customComponentTrackingEventSchema.parse(trackEvent);
          logEvent({
            eventComponent: NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
            eventFeature: NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
            feature: guestComponentSettings?.name || featureName,
            pageComponent: NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
            eventType: eventValidated.eventType,
            clickSubType: eventValidated.clickSubType,
          });
        } catch (e) {
          console.error(
            e instanceof Error
              ? `Invalid tracking event: ${e.message}`
              : 'Invalid tracking event',
          );
        }
      }
    };

    window.addEventListener('message', receiveMessage);

    return () => window.removeEventListener('message', receiveMessage);
  }, [
    guestComponentSettings,
    componentId,
    onIsHelpWidgetOpenChange,
    logEvent,
    featureName,
  ]);

  const postMessage = useCallback(
    (message: unknown) => {
      if (!iframeRef.current) return;
      if (!iframeRef.current.contentWindow) return;
      if (!guestComponentSettings?.url) return;
      iframeRef.current.contentWindow.postMessage(
        message,
        guestComponentSettings.url,
      );
    },
    [guestComponentSettings],
  );

  useEffect(() => {
    if (postMessageRef) postMessageRef(postMessage);
  }, [postMessageRef, postMessage]);

  useEffect(() => {
    const ping = () => {
      postMessage({
        type: 'windowId',
        data: {
          parentWindowId: componentId,
        },
      });
    };

    pingGuestInterval.current = window.setInterval(ping, PING_INTERVAL_MS);

    return () => window.clearInterval(pingGuestInterval.current);
  }, [componentId, guestReadyForMessages, postMessage]);

  useEffect(() => {
    if (!guestReadyForMessages) return;
    window.clearInterval(pingGuestInterval.current);
  }, [guestReadyForMessages]);

  useEffect(() => {
    if (!guestComponentSettings) return;
    if (!guestReadyForMessages) return;

    const globalCSS = globalStyleString(settingsResponse.globalScopeCSS ?? []);
    const themeWithFonts = {
      ...theme,
      ...(globalCSS && { globalScopeCSS: globalCSS }),
    };

    postMessage({
      type: 'settings',
      data: {
        pageContext: {
          product: state.product,
          experienceHost: state.experienceHost,
          retailerMoniker: state.retailerMoniker,
          versionName: state.versionName,
          ...pick(state, guestComponentSettings.scopes),
          jwt,
        },
        layout,
        settings: guestComponentSettings.settings,
        theme: themeWithFonts,
        experiments,
        transferredState,
      },
    });
  }, [
    componentId,
    guestComponentSettings,
    guestReadyForMessages,
    layout,
    postMessage,
    state,
    jwt,
    experiments,
    theme,
    settingsResponse.globalScopeCSS,
    transferredState,
  ]);

  useEffect(() => {
    if (!guestIsLoaded) return;
    logEvent({
      eventComponent: NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
      eventFeature: NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
      feature: guestComponentSettings?.name || featureName,
      pageComponent: NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
      eventType: NarvarEventTypes.COMPONENT_RENDER,
    });
  }, [featureName, guestIsLoaded, logEvent, guestComponentSettings?.name]);

  if (guestComponentSettings === undefined) return null;

  return (
    <iframe
      ref={iframeRef}
      className={styles.iframe}
      // TODO: Set the title of this with a translation
      title="iFrameComponent"
      src={guestComponentSettings.url}
      sandbox="allow-popups allow-scripts allow-same-origin allow-forms allow-presentation allow-top-navigation-by-user-activation"
      style={{
        borderTopLeftRadius,
        borderTopRightRadius,
        borderBottomLeftRadius,
        borderBottomRightRadius,
        display: guestIsLoaded ? 'block' : 'none',
        height: iFrameHeight,
      }}
    />
  );
};

export default CpCustomComponent;
